import { supabase, run } from './db';
import { comprimirImagem } from '../utils/imagemLeve';

/**
 * O comprovante de um recebimento.
 *
 * O lançamento diz que entraram R$ 1.805 no dia 25 por pix. O comprovante é o
 * que prova isso três meses depois, quando o cliente pergunta e a memória de
 * quem lançou já não serve. Hoje esse papel vive no WhatsApp de alguém.
 *
 * O ARQUIVO NÃO VAI PARA A TABELA. Fica no bucket `comprovantes`, que é
 * privado, e na linha fica só o caminho. Guardar bytes em `recebimentos`
 * encareceria todo `select *` que já existe — e são muitos — para servir um
 * arquivo que quase nunca é aberto.
 *
 * A PASTA É O LABORATÓRIO, e não é enfeite de organização: `storage.objects`
 * não tem `laboratory_id`, então é a primeira pasta do caminho que a política
 * de RLS compara com `lab_members`. Caminho fora desse formato é arquivo que
 * ninguém mais consegue ler.
 */

/** O que o bucket aceita. Espelha `allowed_mime_types` da migration. */
export const TIPOS_ACEITOS = [
  'image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif',
  'application/pdf',
];

/** 10 MB: foto de celular com folga, PDF de banco à vontade. */
export const TAMANHO_MAXIMO = 10 * 1024 * 1024;

/**
 * A extensão sai do TIPO, nunca do nome que o arquivo trouxe.
 *
 * Nome de arquivo de celular vem com espaço, acento, parêntese e, no Android,
 * às vezes sem extensão nenhuma ("image"). Como o caminho é montado por nós e
 * o nome original não serve para nada depois, ele não entra.
 */
const EXTENSAO = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/heic': 'heic',
  'image/heif': 'heif',
  'application/pdf': 'pdf',
};

/**
 * O que impede o envio, dito em português.
 *
 * Vale a pena recusar AQUI mesmo o bucket recusando de novo: o erro que o
 * Storage devolve é "mime type not supported", em inglês e sem dizer quais
 * são os suportados. Devolve null quando está tudo bem.
 *
 * TIPO E TAMANHO SE SEPARARAM quando a foto passou a ser reduzida antes de
 * subir. O tipo se sabe na hora de escolher o arquivo; o tamanho só vale
 * depois da compressão, senão uma foto de 15 MB seria recusada sem nem
 * tentar — e ela vira 300 KB.
 */
export const problemaDoTipo = (arquivo) => {
  if (!arquivo) return 'Nenhum arquivo escolhido.';
  if (!TIPOS_ACEITOS.includes(arquivo.type)) {
    return 'O comprovante precisa ser foto (JPG, PNG, WEBP, HEIC) ou PDF.';
  }
  return null;
};

export const problemaDoTamanho = (arquivo, { reduzida = false } = {}) => {
  if (!arquivo) return 'Nenhum arquivo escolhido.';
  if (arquivo.size <= TAMANHO_MAXIMO) return null;
  const mb = (arquivo.size / 1024 / 1024).toFixed(1);
  return reduzida
    ? `Mesmo reduzida, a imagem tem ${mb} MB e o limite é 10 MB.`
    : `O arquivo tem ${mb} MB e o limite é 10 MB.`;
};

export const problemaDoArquivo = (arquivo) =>
  problemaDoTipo(arquivo) || problemaDoTamanho(arquivo);

/**
 * Onde o arquivo mora: `<laboratory_id>/<recebimento_id>.<ext>`.
 *
 * Um comprovante por lançamento, de propósito. Pagamento que chegou em duas
 * transferências são dois lançamentos — é assim que a parcela já trabalha — e
 * aí cada um leva o seu comprovante. Caminho previsível também significa que
 * reenviar SUBSTITUI, em vez de deixar o antigo órfão no bucket.
 */
export const caminhoDoComprovante = (laboratoryId, recebimentoId, tipo) =>
  `${laboratoryId}/${recebimentoId}.${EXTENSAO[tipo] || 'bin'}`;

/**
 * O tipo que o arquivo É, lido dos primeiros bytes — não o que o nome diz.
 *
 * O navegador decide `File.type` pela extensão, e há app de banco que salva
 * o print do pix como "comprovante.pdf" sendo JPEG por dentro. Subia com
 * extensão e tipo de PDF, e o navegador, avisado de que era PDF, falhava ao
 * abrir — com o arquivo inteiro e perfeito no bucket (recebimento de
 * R$ 5.490,00, 28/09).
 *
 * Devolve null quando a assinatura não é nenhuma das conhecidas; aí vale o
 * que o navegador disse.
 */
export const tipoPelosBytes = (b) => {
  if (!b || b.length < 4) return null;
  const ascii = (de, ate) => String.fromCharCode(...b.slice(de, ate));
  if (ascii(0, 4) === '%PDF') return 'application/pdf';
  if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return 'image/jpeg';
  if (b[0] === 0x89 && ascii(1, 4) === 'PNG') return 'image/png';
  if (ascii(0, 4) === 'RIFF' && ascii(8, 12) === 'WEBP') return 'image/webp';
  if (ascii(4, 8) === 'ftyp') {
    const marca = ascii(8, 12);
    if (['heic', 'heix', 'heim', 'heis'].includes(marca)) return 'image/heic';
    if (['mif1', 'msf1', 'heif'].includes(marca)) return 'image/heif';
  }
  return null;
};

/**
 * O arquivo com o tipo corrigido pelo conteúdo, ou ele mesmo.
 *
 * Qualquer falha de leitura devolve o original: não saber confirmar o tipo
 * não é motivo para deixar de subir o comprovante.
 */
/** `Blob.arrayBuffer` onde existe; `FileReader` no Safari antigo, que não tem. */
const lerBytes = (blob) => (typeof blob.arrayBuffer === 'function'
  ? blob.arrayBuffer()
  : new Promise((resolve, reject) => {
      const leitor = new FileReader();
      leitor.onload = () => resolve(leitor.result);
      leitor.onerror = () => reject(leitor.error);
      leitor.readAsArrayBuffer(blob);
    }));

export const comTipoReal = async (arquivo) => {
  try {
    const cabeca = new Uint8Array(await lerBytes(arquivo.slice(0, 16)));
    const real = tipoPelosBytes(cabeca);
    if (!real || real === arquivo.type) return arquivo;
    const nome = String(arquivo.name || 'comprovante').replace(/\.[^.]+$/, '')
      + '.' + (EXTENSAO[real] || 'bin');
    return new File([arquivo], nome, { type: real, lastModified: arquivo.lastModified });
  } catch (e) {
    return arquivo;
  }
};

/**
 * Sobe o arquivo e amarra ao recebimento.
 *
 * Nesta ordem: primeiro o Storage, depois a coluna. O contrário deixaria a
 * linha apontando para um arquivo que talvez não tenha subido — e uma tela
 * dizendo "ver comprovante" que abre em erro é pior do que uma sem anexo.
 */
export const anexarComprovante = async ({ laboratoryId, recebimentoId, arquivo: escolhido }) => {
  // O tipo verdadeiro antes de tudo: é ele que decide se comprime, a
  // extensão do caminho e o `contentType` com que o navegador vai abrir.
  const arquivo = await comTipoReal(escolhido);
  const problema = problemaDoTipo(arquivo);
  if (problema) throw new Error(problema);

  /**
   * A foto encolhe AQUI, e não na tela.
   *
   * São dois caminhos que anexam — o formulário e o "Anexar" da lista — e
   * comprimir num deles é garantir que o outro suba pesado. `comprimirImagem`
   * devolve o próprio arquivo quando não dá ou quando não vale, então PDF e
   * HEIC passam sem nada acontecer.
   */
  const pronto = await comprimirImagem(arquivo);
  const grande = problemaDoTamanho(pronto, { reduzida: pronto !== arquivo });
  if (grande) throw new Error(grande);

  const path = caminhoDoComprovante(laboratoryId, recebimentoId, pronto.type);
  const { error } = await supabase.storage
    .from('comprovantes')
    .upload(path, pronto, { upsert: true, contentType: pronto.type });
  if (error) throw error;

  await run(supabase.from('recebimentos')
    .update({ comprovante_path: path })
    .eq('id', recebimentoId));
  return path;
};

/**
 * Os endereços para abrir, assinados — o bucket é privado e não tem URL fixa.
 *
 * A lista de lançamentos assina TODOS ao carregar, e não no clique. Assinar no
 * clique obrigaria a esperar a resposta para só então abrir a aba — e aba
 * aberta depois de `await` é exatamente o que o bloqueador de pop-up mata.
 * Com o endereço em mãos, o "Ver" é um link comum, que o navegador trata como
 * clique do usuário.
 *
 * Meia hora de validade porque é quanto tempo um modal de conferência fica
 * aberto sem que ninguém recarregue nada.
 *
 * Devolve `{ [path]: url }`, e o que falhar simplesmente não entra no mapa: um
 * comprovante que não assina não pode derrubar a lista de recebimentos.
 */
export const urlsDosComprovantes = async (paths = [], { minutos = 30 } = {}) => {
  const limpos = [...new Set(paths.filter(Boolean))];
  if (limpos.length === 0) return {};
  const { data, error } = await supabase.storage
    .from('comprovantes')
    .createSignedUrls(limpos, minutos * 60);
  if (error) {
    console.warn('[comprovantes] não deu para assinar:', error.message);
    return {};
  }
  const mapa = {};
  (data || []).forEach(item => {
    if (item?.path && item?.signedUrl) mapa[item.path] = item.signedUrl;
  });
  return mapa;
};

/**
 * Tira o comprovante do recebimento: apaga o arquivo e limpa a coluna.
 *
 * O lançamento continua de pé. Trocar um comprovante errado por outro não
 * pode exigir apagar o dinheiro junto.
 */
export const removerComprovante = async (recebimentoId, path) => {
  if (path) await apagarArquivos([path]);
  await run(supabase.from('recebimentos')
    .update({ comprovante_path: null })
    .eq('id', recebimentoId));
};

/**
 * Apaga arquivos soltos, para quando a LINHA é que vai embora.
 *
 * Não existe cascata do banco para o Storage: apagar o recebimento deixaria o
 * arquivo lá, invisível e pago para sempre. Quem apaga linha com comprovante
 * chama isto antes.
 *
 * Falha em silêncio porque a ordem de importância é clara: o que o usuário
 * pediu foi apagar o lançamento, e um arquivo órfão no bucket não é razão para
 * recusar. O erro vai para o console, onde o desenvolvimento o encontra.
 */
export const apagarArquivos = async (paths = []) => {
  const limpos = paths.filter(Boolean);
  if (limpos.length === 0) return;
  const { error } = await supabase.storage.from('comprovantes').remove(limpos);
  if (error) console.warn('[comprovantes] não deu para apagar o arquivo:', error.message);
};
